import type {ComponentStageData, ComponentStageHeuristicDefinition, ReadOnlyExtensionAPI} from '../../types'
import {
    updateTargetLayout,
    isSlottedComponent,
    isHeaderContainer,
    isEmptyBox,
    isEmptyRootContainer,
    isSuperGrid,
    isSuperGridCell,
    isCssGrid
} from '../common'
import {
    getTargetLayoutPointer,
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer
} from '../../conversionDal/utils/pointer'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {OrderPluginAPI} from '../order/types'
import {
    fractionRowDefinition,
    minContentRowDefinition,
    minMaxRowDefinition,
    cssGridLargeRowDefinition,
    cssGridDefaultRowDefinition
} from './constants'
import _ from 'lodash'
import type {GridTrackSize, MinMaxSize} from '@wix/document-services-types'

type ArrayMapFn<T, U> = (value: T, index: number, array: T[]) => U

const updateContainerRows = (
    componentStageData: ComponentStageData,
    rowDefinition: GridTrackSize | ArrayMapFn<any, GridTrackSize>,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => {
    const {componentPointer, conversionDal, globalContext} = componentStageData
    const {order} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const orderApi = order as OrderPluginAPI
    const rowDefinitionCallback = _.isFunction(rowDefinition) ? rowDefinition : () => rowDefinition
    let targetRowCount = orderApi.getContainerRowCount(componentPointer)

    if (!targetRowCount) {
        targetRowCount = 1
    }

    const {containerLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}
    const newRows = Array.from({length: targetRowCount}).map(rowDefinitionCallback)

    updateTargetLayout(componentStageData, {
        containerLayout: {
            ...containerLayout,
            rows: newRows,
            type: 'GridContainerLayout'
        }
    })
}

const updateContainerRowsAdaptive = (
    componentStageData: ComponentStageData,
    rowDefinition: GridTrackSize | ArrayMapFn<any, GridTrackSize>,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI,
    shouldResetExistingRows = false
) => {
    const {componentPointer, conversionDal, globalContext} = componentStageData
    const {order} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const orderApi = order as OrderPluginAPI
    const finalRowCount = orderApi.getContainerRowCount(componentPointer)

    if (finalRowCount === undefined) {
        return
    }

    const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}
    const currentRows = containerLayout?.rows ?? []
    const rowDefinitionCallback = _.isFunction(rowDefinition) ? rowDefinition : () => rowDefinition
    let newRows

    if (shouldResetExistingRows) {
        newRows = Array(finalRowCount).fill(undefined).map(rowDefinitionCallback)
    } else if (finalRowCount > currentRows.length) {
        newRows = [
            ...currentRows,
            ...Array(finalRowCount - currentRows.length)
                .fill(undefined)
                .map(rowDefinitionCallback)
        ]
    } else {
        return
    }

    updateTargetLayout(componentStageData, {
        containerLayout: {
            ...containerLayout,
            rows: newRows,
            type: 'GridContainerLayout'
        }
    })
}

const createMinHeightRowDefinition = (height: number): MinMaxSize => ({
    type: 'MinMaxSize',
    min: {
        value: height,
        type: 'spx'
    },
    max: {
        type: 'auto'
    }
})

const updateContainerWithMinHeightRow = (
    componentStageData: ComponentStageData,
    height: number,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI,
    shouldSetMinHeightToRow: ArrayMapFn<any, GridTrackSize> = () => createMinHeightRowDefinition(height)
) => {
    updateContainerRows(componentStageData, shouldSetMinHeightToRow, readOnlyExtensionAPI)
}

const isEmptySmallBox = (componentStageData: ComponentStageData) => {
    const {componentPointer, initialSnapshot} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)

    return isEmptyBox(componentStageData) && originalMeasurements.height < 101
}

const isContainer = ({componentPointer, conversionDal}: ComponentStageData) =>
    conversionDal.get(componentPointer).type === 'Container'

const buildCssGridRowStretchMap = (componentStageData: ComponentStageData): Map<number, boolean> => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)
    const children: string[] = component?.components ?? []
    const stretchMap = new Map<number, boolean>()

    for (const childId of children) {
        const childPointer = getStructurePointer({id: childId})
        const targetLayout = conversionDal.get(getTargetLayoutPointer(childPointer))
        const sourceLayout = conversionDal.get(getSourceLayoutPointer(childPointer))
        const rowStart = targetLayout?.itemLayout?.gridArea?.rowStart
        if (rowStart !== undefined && sourceLayout?.itemLayout?.alignSelf === 'stretch') {
            stretchMap.set(rowStart, true)
        }
    }

    return stretchMap
}

export const rowHeuristicsFlush: ComponentStageHeuristicDefinition[] = [
    {
        predicate: isSlottedComponent,
        apply: () => {},
        heuristicCode: 'SLOTTED COMPONENT SKIP'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isHeaderContainer({componentPointer, conversionDal})
        },
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            const shouldSetMinHeightToRow = (value: any, index: number) => {
                if (index === 0) {
                    return createMinHeightRowDefinition(70)
                }
                return minContentRowDefinition
            }
            updateContainerWithMinHeightRow(componentStageData, 70, readOnlyExtensionAPI!, shouldSetMinHeightToRow)
        },
        heuristicCode: 'HEADER CONTAINER FLUSH'
    },
    {
        predicate: isEmptyRootContainer,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithMinHeightRow(componentStageData, 200, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY ROOT CONTAINER ROWS FLUSH'
    },
    {
        predicate: isEmptySmallBox,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithMinHeightRow(componentStageData, 100, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY SMALL BOX ROWS FLUSH'
    },
    {
        predicate: isEmptyBox,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithMinHeightRow(componentStageData, 200, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY LARGE BOX ROWS FLUSH'
    },
    {
        predicate: isSuperGrid,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRows(componentStageData, minMaxRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'SUPERGRID ROWS FLUSH'
    },
    {
        predicate: isCssGrid,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            const stretchMap = buildCssGridRowStretchMap(componentStageData)
            updateContainerRows(
                componentStageData,
                (_value, index) =>
                    stretchMap.has(index + 1) ? cssGridLargeRowDefinition : cssGridDefaultRowDefinition,
                readOnlyExtensionAPI!
            )
        },
        heuristicCode: 'CSSGRID ROWS FLUSH'
    },
    {
        predicate: isSuperGridCell,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRows(componentStageData, fractionRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'SUPERGRIDCELL ROWS FLUSH'
    },
    {
        predicate: isContainer,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRows(
                componentStageData,
                (_value, index, rows) => {
                    if (index === rows.length - 1) {
                        return fractionRowDefinition
                    }

                    return minContentRowDefinition
                },
                readOnlyExtensionAPI!
            )
        },
        heuristicCode: 'DEFAULT ROWS FLUSH'
    }
]

export const rowHeuristicsAdaptive: ComponentStageHeuristicDefinition[] = [
    {
        predicate: isSlottedComponent,
        apply: () => {},
        heuristicCode: 'SLOTTED COMPONENT SKIP'
    },
    {
        predicate: isSuperGrid,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRowsAdaptive(componentStageData, minMaxRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'SUPERGRID ROWS ADAPTIVE'
    },
    {
        predicate: isSuperGridCell,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRowsAdaptive(componentStageData, fractionRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'SUPERGRIDCELL ROWS ADAPTIVE'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const targetLayout = initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))
            return isEmptySmallBox(componentStageData) && _.isEmpty(targetLayout?.containerLayout)
        },
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithMinHeightRow(componentStageData, 100, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY SMALL BOX ROWS ADAPTIVE'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const targetLayout = initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))
            return isEmptyBox(componentStageData) && _.isEmpty(targetLayout?.containerLayout)
        },
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithMinHeightRow(componentStageData, 200, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY LARGE BOX ROWS ADAPTIVE'
    },
    {
        predicate: isCssGrid,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            const {componentPointer, conversionDal} = componentStageData
            const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}
            const currentRowCount = containerLayout?.rows?.length ?? 0
            const stretchMap = buildCssGridRowStretchMap(componentStageData)
            updateContainerRowsAdaptive(
                componentStageData,
                (_value, index) =>
                    stretchMap.has(index + currentRowCount + 1)
                        ? cssGridLargeRowDefinition
                        : cssGridDefaultRowDefinition,
                readOnlyExtensionAPI!
            )
        },
        heuristicCode: 'CSSGRID ROWS ADAPTIVE'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            return isContainer(componentStageData) && !isEmptyBox(componentStageData)
        },
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRowsAdaptive(
                componentStageData,
                (_value, index, rows) => {
                    if (index === rows.length - 1) {
                        return fractionRowDefinition
                    }

                    return minContentRowDefinition
                },
                readOnlyExtensionAPI!,
                true
            )
        },
        heuristicCode: 'DEFAULT ROWS ADAPTIVE'
    }
]
